import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { CheckCircle, AlertCircle, Image as ImageIcon, Calendar, MapPin, Clock, ChevronDown } from 'lucide-react';
import '../styles/ArtworkAcceptance.css';

const ArtworkAcceptance = () => {
  const { acceptanceUuid } = useParams();
  
  // State management
  const [collection, setCollection] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [artist, setArtist] = useState(null);
  const [submissionMapping, setSubmissionMapping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptanceSubmitted, setAcceptanceSubmitted] = useState(false);
  
  // Acceptance terms state
  const [termsAccepted, setTermsAccepted] = useState({
    originalCreation: false,
    artistAgreement: false,
    exhibitionRights: false
  });
  const [showFullTerms, setShowFullTerms] = useState(false);

  useEffect(() => {
    if (acceptanceUuid) {
      loadAcceptanceData();
    }
  }, [acceptanceUuid]);

  const loadAcceptanceData = async () => {
    try {
      setLoading(true);
      
      // Get the submission mapping record using the acceptance UUID
      const { data: mappingData, error: mappingError } = await supabase
        .from('artist_submission_mapping')
        .select(`
          *,
          users (
            uuid,
            first_name,
            last_name,
            nickname,
            email
          )
        `)
        .eq('acceptance_uuid', acceptanceUuid)
        .single();

      if (mappingError) {
        if (mappingError.code === 'PGRST116') {
          // Record doesn't exist - this might be a direct acceptance link
          // We'll need to get the data from URL parameters or create a basic record
          console.log('No mapping record found for acceptance_uuid:', acceptanceUuid);
          
          // For now, show an error - the admin needs to create the mapping record first
          throw new Error('Acceptance link not found. Please contact the administrator to set up your acceptance record.');
        }
        throw mappingError;
      }

      setSubmissionMapping(mappingData);
      setArtist(mappingData.users);

              // Get collection details from the submission
        if (mappingData.submission_id) {
          const { data: submissionData, error: submissionError } = await supabase
            .from('submissions_details')
            .select('collection_id, acceptance_terms')
            .eq('submission_id', mappingData.submission_id)
            .single();

        if (submissionError) throw submissionError;

        // Update submissionMapping with submission details including acceptance_terms
        setSubmissionMapping(prev => ({
          ...prev,
          submission_details: submissionData
        }));
        
        // Debug: Log the submission data to see what we're getting
        console.log('Submission data loaded:', submissionData);
        console.log('Acceptance terms:', submissionData?.acceptance_terms);

        if (submissionData?.collection_id) {
          // Load collection and artworks
          await loadCollectionAndArtworks(submissionData.collection_id, mappingData.users?.uuid);
        }
      } else {
        throw new Error('No submission found for this acceptance link');
      }
    } catch (err) {
      console.error('Error loading acceptance data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCollectionAndArtworks = async (collectionId, artistUuid) => {
    try {
      // Get collection details
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .select(`
          *,
          events (
            *,
            galleries:gallery_id (
              name,
              address,
              website,
              contact_email,
              contact_phone
            )
          )
        `)
        .eq('collection_id', collectionId)
        .single();

      if (collectionError) throw collectionError;
      setCollection(collectionData);

      // Get accepted artworks for this collection
      const { data: artworkData, error: artworkError } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          accepted,
          order_num,
          vcs (
            vc_id,
            title,
            description,
            cover_image,
            width,
            height,
            depth,
            price,
            users (
              uuid,
              first_name,
              last_name,
              nickname,
              email
            )
          )
        `)
        .eq('collection_id', collectionId)
        .eq('accepted', true)
        .order('order_num', { ascending: true, nullsFirst: false });

      if (artworkError) throw artworkError;
      
      // Filter artworks for this specific artist
      const artistArtworks = artworkData.filter(artwork => 
        artwork.vcs?.users?.uuid === artistUuid
      );
      
      setArtworks(artistArtworks || []);
    } catch (err) {
      console.error('Error loading collection and artworks:', err);
      setError(err.message);
    }
  };



  const handleTermsChange = (term, value) => {
    setTermsAccepted(prev => ({
      ...prev,
      [term]: value
    }));
  };

  const allTermsAccepted = Object.values(termsAccepted).every(term => term);

  const handleAcceptanceSubmit = async () => {
    if (!allTermsAccepted) {
      alert('Please accept all terms before submitting.');
      return;
    }

    console.log('Starting acceptance submission...');
    console.log('Acceptance UUID:', acceptanceUuid);
    console.log('Terms accepted:', termsAccepted);
    console.log('Submission mapping:', submissionMapping);

    try {
      // First, check if the submission mapping record exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('artist_submission_mapping')
        .select('id, submission_id, user_id')
        .eq('acceptance_uuid', acceptanceUuid)
        .single();

      console.log('Check result:', { existingRecord, checkError });

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking existing record:', checkError);
        throw new Error('Failed to check existing acceptance record');
      }

      let result;
      
      if (existingRecord) {
        // Update existing record
        console.log('Updating existing record:', existingRecord.id);
        result = await supabase
          .from('artist_submission_mapping')
          .update({
            terms_accepted: termsAccepted,
            accepted_at: new Date().toISOString(),
            acceptance_ip: null, // Could be added later if needed
            acceptance_user_agent: navigator.userAgent
          })
          .eq('id', existingRecord.id);
      } else {
        // Create new record if none exists
        console.log('Creating new record for acceptance_uuid:', acceptanceUuid);
        
        // We need to get the submission_id from the collection data
        if (!submissionMapping) {
          throw new Error('No submission mapping data available');
        }
        
        result = await supabase
          .from('artist_submission_mapping')
          .insert({
            submission_id: submissionMapping.submission_id,
            user_id: submissionMapping.user_id,
            acceptance_uuid: acceptanceUuid,
            terms_accepted: termsAccepted,
            accepted_at: new Date().toISOString(),
            acceptance_ip: null,
            acceptance_user_agent: navigator.userAgent,
            paid: true, // Assume true for acceptance
            accepted: true
          });
      }

      console.log('Database operation result:', result);

      if (result.error) {
        console.error('Error saving acceptance:', result.error);
        throw new Error('Failed to save acceptance to database');
      }

      console.log('Acceptance saved successfully:', result);
      setAcceptanceSubmitted(true);
      
      // Show success message
      setTimeout(() => {
        setAcceptanceSubmitted(false);
      }, 5000);

    } catch (err) {
      console.error('Error submitting acceptance:', err);
      alert('Error submitting acceptance. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="artwork-acceptance-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading acceptance details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="artwork-acceptance-page">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Error Loading Page</h2>
          <p>{error}</p>
          <p>Please check the link or contact support.</p>
        </div>
      </div>
    );
  }

  if (acceptanceSubmitted) {
    return (
      <div className="artwork-acceptance-page">
        <div className="success-container">
          <CheckCircle className="success-icon" />
          <h2>Acceptance Submitted!</h2>
          <p>Thank you for accepting the terms for "{collection?.title}".</p>
          <p>We'll be in touch with further details about the exhibition.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="artwork-acceptance-page">
      {/* Header */}
      <div className="acceptance-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Artwork Acceptance</h1>
            <p>Please review and accept the terms for your accepted artwork</p>
          </div>
        </div>
      </div>

      {/* Collection Hero */}
      {collection && (
        <div className="collection-hero">
          <div className="hero-content">
            <div className="hero-info">
              <h2>{collection.title}</h2>
              <p>{collection.description}</p>
              
              {/* Event details if available */}
              {collection.events && collection.events.length > 0 && (
                <div className="event-details">
                  {collection.events[0].galleries && (
                    <div className="event-info">
                      <MapPin className="event-icon" />
                      <span>{collection.events[0].galleries.name}</span>
                    </div>
                  )}
                  {collection.events[0].setup_time && (
                    <div className="event-info">
                      <Clock className="event-icon" />
                      <span>Setup: {collection.events[0].setup_time}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Collection cover image */}
            {collection.cover_image && (
              <div className="hero-image-container">
                <img 
                  src={collection.cover_image} 
                  alt={collection.title}
                  className="hero-cover-image"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Artist Welcome */}
      {artist && (
        <div className="artist-welcome">
          <div className="welcome-content">
            <h3>Congratulations, {artist.nickname || `${artist.first_name} ${artist.last_name}`}!</h3>
            <p>Your artwork has been accepted for "{collection?.title}". Please review the terms below and confirm your acceptance.</p>
          </div>
        </div>
      )}

      {/* Accepted Artworks */}
      {artworks.length > 0 && (
        <div className="accepted-artworks">
          <h3>Your Accepted Artworks</h3>
          <div className="artworks-grid">
            {artworks.map((item, index) => (
              <div key={item.vc_id} className="artwork-card">
                <div className="artwork-image">
                  {item.vcs.cover_image ? (
                    <img 
                      src={item.vcs.cover_image} 
                      alt={item.vcs.title || "Accepted artwork"}
                      className="artwork-thumbnail"
                    />
                  ) : (
                    <div className="artwork-placeholder">
                      <ImageIcon />
                    </div>
                  )}
                </div>
                <div className="artwork-details">
                  {item.vcs.title && <h4>{item.vcs.title}</h4>}
                  {(item.vcs.width || item.vcs.height) && (
                    <p className="dimensions">
                      {item.vcs.width && item.vcs.height 
                        ? `${item.vcs.width}" × ${item.vcs.height}"${item.vcs.depth ? ` × ${item.vcs.depth}"` : ''}`
                        : item.vcs.width ? `${item.vcs.width}" wide` : `${item.vcs.height}" tall`
                      }
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Acceptance Terms */}
      <div className="acceptance-terms">
        <h3>Artist Agreement</h3>
        <p>By checking the boxes below, you agree to the following terms:</p>
        
        <div className="terms-list">
          <div className="term-item">
            <label className="term-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted.originalCreation}
                onChange={(e) => handleTermsChange('originalCreation', e.target.checked)}
              />
              <span className="checkmark"></span>
            </label>
            <div className="term-text">
              <strong>The artwork is your original creation.</strong>
              <p>You confirm that the artwork is your own original work and you have the rights to exhibit it.</p>
            </div>
          </div>

          <div className="term-item">
            <label className="term-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted.exhibitionRights}
                onChange={(e) => handleTermsChange('exhibitionRights', e.target.checked)}
              />
              <span className="checkmark"></span>
            </label>
            <div className="term-text">
              <strong>You grant exhibition rights for the duration of the show.</strong>
              <p>You authorize VibrantCast to display your artwork during the exhibition period.</p>
            </div>
          </div>

          <div className="term-item">
            <label className="term-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted.artistAgreement}
                onChange={(e) => handleTermsChange('artistAgreement', e.target.checked)}
              />
              <span className="checkmark"></span>
            </label>
            <div className="term-text">
              <strong>I agree to the artist agreement.</strong>
              <p>I have read and agree to the full artist agreement below.</p>
            </div>
          </div>
        </div>

        {/* Full Terms Expand/Collapse */}
        {/* Debug: Log what we have */}
        {console.log('Render debug - submissionMapping:', submissionMapping)}
        {console.log('Render debug - acceptance_terms:', submissionMapping?.submission_details?.acceptance_terms)}
        
        {submissionMapping?.submission_details?.acceptance_terms && (
          <div className="full-terms-section">
            <button
              type="button"
              onClick={() => setShowFullTerms(!showFullTerms)}
              className="expand-terms-button"
            >
              <span>{showFullTerms ? 'Hide' : 'Show'} Full Artist Agreement</span>
              <ChevronDown 
                className={`expand-icon ${showFullTerms ? 'expanded' : ''}`} 
              />
            </button>
            
            {showFullTerms && (
              <div className="full-terms-content">
                <div 
                  className="terms-html-content"
                  dangerouslySetInnerHTML={{ 
                    __html: submissionMapping.submission_details.acceptance_terms 
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div className="acceptance-actions">
          <button
            onClick={handleAcceptanceSubmit}
            disabled={!allTermsAccepted}
            className={`acceptance-button ${allTermsAccepted ? 'enabled' : 'disabled'}`}
          >
            <CheckCircle />
            Accept Terms & Confirm Participation
          </button>
          
          {!allTermsAccepted && (
            <p className="terms-reminder">
              Please accept all terms to continue
            </p>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="contact-info">
        <h3>Questions?</h3>
        <p>If you have any questions about the exhibition or need assistance, please contact us:</p>
                  <div className="contact-details">
            <p>📧 <a href="mailto:support@vibrantcast.com">support@vibrantcast.com</a></p>
            <p>📱 <a href="https://www.instagram.com/vibrant.cast/" target="_blank" rel="noopener noreferrer">@vibrant.cast on Instagram</a></p>
          </div>
      </div>
    </div>
  );
};

export default ArtworkAcceptance;
